import React from "react";
import { CanvasToolsProps, LightMode } from "../../types";
import { useLanguage } from "../../contexts/LanguageContext";
import ColorPicker from "./ColorPicker";
import "./CanvasTools.css";

const LIGHT_MODES: LightMode[] = ["studio", "top", "side"];

interface ExtendedCanvasToolsProps extends CanvasToolsProps {
  drawingColor: string;
  onColorChange: (color: string) => void;
  lightMode: LightMode;
  onLightModeChange: (mode: LightMode) => void;
  lightIntensity: number;
  onLightIntensityChange: (intensity: number) => void;
}

const LightModeIcon: React.FC<{ mode: LightMode }> = ({ mode }) => {
  if (mode === "top") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M12 3v8M8 5.5 12 3l4 2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 18h14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (mode === "side") {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M3 12h8M5.5 8 3 12l2.5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 5v14"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6.1 6.1l1.6 1.6M16.3 16.3l1.6 1.6M17.9 6.1l-1.6 1.6M7.7 16.3l-1.6 1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
};

const CanvasTools: React.FC<ExtendedCanvasToolsProps> = ({
  currentTool,
  onToolChange,
  onClearDrawing,
  onTakeScreenshot,
  drawingColor,
  onColorChange,
  lightMode,
  onLightModeChange,
  lightIntensity,
  onLightIntensityChange,
}) => {
  const { t } = useLanguage();
  const nextLightMode =
    LIGHT_MODES[(LIGHT_MODES.indexOf(lightMode) + 1) % LIGHT_MODES.length];

  return (
    <div className="canvas-tools">
      <button
        className={`tool-btn ${currentTool === "pencil" ? "active" : ""}`}
        onClick={() => onToolChange(currentTool === "pencil" ? null : "pencil")}
        title={t("draw")}
      >
        ✏️
      </button>

      <ColorPicker currentColor={drawingColor} onColorChange={onColorChange} />

      <button
        className="tool-btn"
        onClick={() => {
          if (window.clearDrawingFunction) {
            window.clearDrawingFunction();
          } else {
            onClearDrawing();
          }
        }}
        title={t("clear_drawing")}
      >
        🗑️
      </button>

      <button
        className="tool-btn"
        onClick={() => {
          if (window.screenshotFunction) {
            window.screenshotFunction();
          } else {
            onTakeScreenshot();
          }
        }}
        title={t("take_screenshot")}
      >
        📷
      </button>

      <div className="light-controls">
        <button
          className={`tool-btn light-mode-btn${
            lightMode === "studio" ? "" : " is-custom"
          }`}
          onClick={() => onLightModeChange(nextLightMode)}
          title={`${t("lighting")}: ${t(`light_${lightMode}`)}`}
          aria-label={`${t("lighting")}: ${t(`light_${lightMode}`)}`}
        >
          <LightModeIcon mode={lightMode} />
        </button>
        <label
          className="light-intensity"
          title={`${t("light_intensity")}: ${Math.round(lightIntensity * 100)}%`}
        >
          <input
            type="range"
            min={0}
            max={200}
            step={1}
            value={Math.round(lightIntensity * 100)}
            aria-label={t("light_intensity")}
            onChange={(event) =>
              onLightIntensityChange(Number(event.target.value) / 100)
            }
          />
        </label>
      </div>
    </div>
  );
};

export default CanvasTools;
